import type { HeadingLevel } from '../components/Heading/index.vue'
import type { Size } from '../components/Text/index.vue'
// import { ElementalTypeEnum } from '../types/elemental'
export enum ElementalTypeEnum {
  List = 0,
  Prompt = 1,
  Snippet = 2,
  Document = 3,
  Output = 4,
  Automation = 5,
  Course = 6,
  Table = 7,
  Cell = 8,
  Agent = 9,
  Tag = 10,
  Image = 11,
  unknown = 404,
}
export type ElementalColor = [`#${string}`, `#${string}`]

export const ElementalColors: Partial<Record<ElementalTypeEnum, ElementalColor>> = {
  [ElementalTypeEnum.Prompt]: ['#FF88EC', '#FFDDFA'],
  [ElementalTypeEnum.Snippet]: ['#A993FF', '#a993ff4d'],
  [ElementalTypeEnum.Document]: ['#6ed8fb', '#6ed8fb4d'],
  [ElementalTypeEnum.Automation]: ['#fbbf24', '#fbbf244d'],
  [ElementalTypeEnum.Table]: ['#7EE6AE', '#7ee6ae4d'],
  [ElementalTypeEnum.List]: ['#FFC69A', '#FFC69A4d'],
  [ElementalTypeEnum.Cell]: ['#7EE6AE', '#7ee6ae4d'],
  [ElementalTypeEnum.Agent]: ['#9747FF', '#9747ff4d'],
  [ElementalTypeEnum.Image]: ['#D771FF', '#D771FF33'],
}

export const getElementTypeColor = (type: ElementalTypeEnum) => {
  const color = ElementalColors[type]
  if (!color)
    return ['#21242C', '#F6F6FF']

  return color
}

export function useTheme() {
  const fontSizes: Record<`heading-${HeadingLevel}`, Size> = {
    'heading-1': '32px',
    'heading-2': '24px',
    'heading-2-light': '24px',
    'heading-3': '20px',
    'heading-3-light': '20px',
    'heading-4': '16px',
    'heading-4-light': '16px',
    'heading-5': '14px',
    'heading-5-light': '14px',
    'heading-6': '10px',
  }

  return {
    colors: {
      // shadcn
      border: 'hsl(var(--border))',
      input: 'hsl(var(--input))',
      ring: 'hsl(var(--ring))',
      background: 'hsl(var(--background))',
      foreground: 'hsl(var(--foreground))',
      secondary: {
        DEFAULT: 'hsl(var(--secondary))',
        foreground: 'hsl(var(--secondary-foreground))',
      },
      destructive: {
        DEFAULT: 'hsl(var(--destructive))',
        foreground: 'hsl(var(--destructive-foreground))',
      },
      muted: {
        DEFAULT: 'hsl(var(--muted))',
        foreground: 'hsl(var(--muted-foreground))',
      },
      accent: {
        DEFAULT: 'hsl(var(--accent))',
        foreground: 'hsl(var(--accent-foreground))',
      },
      popover: {
        DEFAULT: 'hsl(var(--popover))',
        foreground: 'hsl(var(--popover-foreground))',
      },
      card: {
        DEFAULT: 'hsl(var(--card))',
        foreground: 'hsl(var(--card-foreground))',
      },

      // Figma names:
      spGray1: '#F6F6FF',
      spGray2: '#E3E9F8',
      spGray3: '#A8B6D8',
      spGray4: '#4B587C',
      spBgPurple: '#E6E0FE',

      white: '#FFFFFF',
      transparentWhite: 'rgba(255, 255, 255, 0.85)',
      brandGray: '#F7F7F8',
      grayLighter: '#F6F6FF', // gray1
      softGray: '#E3E9F8', // gray2
      darkGray: '#A8B6D8', // gray3
      darkerGray: '#4B587C', // gray4
      light: '#EFF1F5',
      lightBluish: '#E3E8F8',
      bluish: '#D0D7E8',
      bluishSofter: '#A8B6D8',
      anotherDark: '#8D94A6',
      dark: '#8C92A1',
      bluishDark: '#727C97',
      bluishDarker: '#4B587C',
      softBlack: '#21242C',
      black: '#121212',
      transparentBlack: 'rgba(18, 18, 18, 0.40)',
      orange: '#EF7272',
      darkPurple: '#9747FF',
      purple: '#A993FF',
      purple2: '#A993FF33',
      green2: '#51EB85',
      blue: '#6D56ED',
      primary: '#FF88EC',
      primaryLight: '#FFDDFA',
      chatGPT: '#29C27F',
      chatGPTActive: '#25A56D',
      gemini: '#2376E5',
      geminiActive: '#1056B2',
      chatElunaActive:
        'linear-gradient(87.56deg, #D93AC2 5.26%, #A65FEE 95.03%)',
      admin: '#FBBF24',
      yellowStar: '#FFC83B',
      yellowLight: '#E6FB93',
      purple1: '#5824FB',

      elemental: {
        tables: {
          bg: 'rgba(125, 221, 146, 0.1)',
          bgIconCardDemo: 'rgba(125, 221, 146, 0.25)',
          color: 'rgba(125, 221, 146, 1)',
        },
        list: {
          bg: 'rgba(95, 238, 219, 0.25)',
          bgIconCardDemo: 'rgba(95, 238, 219, 0.25)',
          color: 'rgba(95, 238, 219, 1)',
        },
        prompt: {
          bg: 'rgba(255, 136, 236, 0.25)',
          bgIconCardDemo: 'rgba(255, 136, 236, 0.25)',
          color: 'rgba(255, 136, 236, 1)',
        },
        explore: {
          bg: 'rgba(169, 147, 255, 0.1)',
          bgIconCardDemo: 'rgba(169, 147, 255, 0.25)',
          color: 'rgba(169, 147, 255, 1)',
        },
        automation: {
          bg: 'rgba(169, 147, 255, 0.25)',
          bgIconCardDemo: 'rgba(169, 147, 255, 0.25)',
          color: 'rgba(169, 147, 255, 1)',
        },
        marketplace: {
          bg: 'rgba(255, 136, 236, 0.10)',
          bgIconCardDemo: 'rgba(255, 136, 236, 0.25)',
          color: 'rgba(255, 136, 236, 1)',
        },
        comingSoon: {
          bg: 'rgba(168, 182, 216, 0.12)',
          bgIconCardDemo: 'rgba(168, 182, 216, 0.25)',
          color: 'rgba(168, 182, 216, 1)',
        },
      },
    },

    variableColors: [
      ['#FFBF43', '#FFEFC0'],
      ['#FA5892', '#FFC0CF'],
      ['#4399FF', '#C0E8FF'],
      ['#70C548', '#DCFFC0'],
    ],

    fontSizes,
    borderRadius: {
      lg: 'var(--radius)',
      md: 'calc(var(--radius) - 2px)',
      sm: 'calc(var(--radius) - 4px)',
    },
  }
}

export type Color = keyof ReturnType<typeof useTheme>['colors']

export function getVariableColor(index: number) {
  const { variableColors } = useTheme()
  const color = variableColors[index % variableColors.length]

  const isRightFormat
    = color && color.length >= 2 && color.every(c => typeof c === 'string')

  if (!isRightFormat)
    throw new Error(`Invalid color format: ${color}`)

  return color.slice(0, 2) as [string, string]
}
